import { ARCHIVE, DRAFT, ERRORS, INBOX, SENT } from '@constants/documents.constants';
import { createSelector } from '@reduxjs/toolkit';
import { selectUserSettings } from '@store/user/user.selector';

import { State } from '../reducer';

import { DocumentDashboardType, DocumentsStateFromType } from './documents.state';

/*
 * ROOT SELECTORS
 */

/**
 * Get a documents state
 */
export function selectDocumentsState<T extends DocumentDashboardType>(state: State, docType: T) {
  const subState = {
    [INBOX]: state.documentInbox,
    [ARCHIVE]: state.documentArchived,
    [DRAFT]: state.documentDraft,
    [SENT]: state.documentSent,
    [ERRORS]: state.documentErrored,
  };

  return subState[docType] as DocumentsStateFromType<T>;
}

/*
 * DASHBOARDS
 */

/**
 * Get the total number of documents for the specified dashboard
 */
export const selectDashboardTotalDocuments = createSelector(
  selectDocumentsState,
  (documentsState) => documentsState.dashboardView.total,
);

/**
 * Get search parameters for the dashboard view
 */
export const selectDashboardSearchParams = createSelector(
  selectDocumentsState,
  (documentsState) => documentsState?.dashboardView.searchParams,
);

/**
 * Does the Lifen Sending dashboard has {@link DashboardSearchParams} parameters
 */
export const selectDashboardHasSearchParams = (state: State, docType: DocumentDashboardType) => {
  const params = selectDashboardSearchParams(state, docType);
  if (!params) return false;
  return Boolean(
    params.selectedMediumFilter ||
      params.selectedStatusFilter ||
      params.selectedSenderFilter ||
      params.hideOtherRequesters ||
      params.defaultDateRange ||
      params.dateRange ||
      params.recipient ||
      params.searchPatient ||
      params.source,
  );
};

export const selectReadyToSendAutomaticSending = (state: State) =>
  selectUserSettings(state).readyToSendAutomaticSending;

export const selectDashboardIsLoading = createSelector(
  selectDocumentsState,
  (documentsState) => documentsState.dashboardView.documentsRequest.isLoading,
);
